import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel
} from '@microsoft/signalr';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { environment } from '@enviroment/environment';
import { AuthManagementService } from '@core/service/auth-management.service';
import { SimpleNotification } from '@core/models/notifications/simple-notification';
import {
  NotificationDetailDialogComponent
} from "@shared/components/notification-detail-dialog/notification-detail-dialog.component";
import {MatDialog} from "@angular/material/dialog";

type StoredNotification = SimpleNotification & {
  _id: string;          // id generado en front
  _ts: number;          // timestamp
  _read: boolean;       // leída o no
};

@Injectable({ providedIn: 'root' })
export class NotificationRealtimeService {

  private connection: HubConnection | null = null;
  private startingPromise: Promise<void> | null = null;
  private tokenWatcherSub: Subscription | null = null;

  // ================================
  // Storage Keys
  // ================================
  private readonly STORAGE_KEY = 'rmd_notifications_v1';

  // ================================
  // Estado observable
  // ================================
  private readonly _notifications$ = new BehaviorSubject<StoredNotification[]>([]);
  readonly notifications$ = this._notifications$.asObservable();

  private readonly _unreadCount$ = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this._unreadCount$.asObservable();

  private readonly _connected$ = new BehaviorSubject<boolean>(false);
  readonly connected$ = this._connected$.asObservable();

  constructor(
    private auth: AuthManagementService,
    private snack: MatSnackBar,
    private zone: NgZone,
    private dialog: MatDialog,
  ) {
    // ✅ cargar persistencia al construir el service (soporta refresh)
    this.restoreFromStorage();
  }

  // ==========================================================
  // INIT (llamar una sola vez desde AppComponent)
  // ==========================================================
  init(): void {
    this.ensureConnected();

    if (!this.tokenWatcherSub) {
      this.tokenWatcherSub = fromEvent<StorageEvent>(window, 'storage')
        .subscribe((ev) => {
          if (!ev.key) return;

          const watchedKeys = [environment.cypher_key, environment.user_data];
          if (watchedKeys.includes(ev.key)) {
            // ✅ si deja de ser médico (otro login), desconecta
            if (!this.auth.isUserDoctor()) {
              this.disconnect();
              return;
            }
            this.ensureConnected();
          }
        });
    }
  }

  // ==========================================================
  // Conexión segura
  // ==========================================================
  ensureConnected(): void {
    const token = this.getJwtToken();

    if (!token) {
      this.disconnect();
      return;
    }

    if (this.connection && this.connection.state === HubConnectionState.Connected) {
      return;
    }

    if (this.startingPromise) {
      return;
    }

    this.connect().catch(err => {
    });
  }

  safeEnsureConnected(): void {
    this.ensureConnected();
  }

  // ==========================================================
  // CONNECT
  // ==========================================================
  async connect(): Promise<void> {

    const token = this.getJwtToken();
    if (!token) return;

    if (this.connection) {
      try { await this.connection.stop(); } catch {}
      this.connection = null;
    }

    const hubUrl = this.buildHubUrl();

    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
        accessTokenFactory: () => this.getJwtToken() ?? ''
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
      .configureLogging(LogLevel.Information)
      .build();

    this.registerHandlers(this.connection);

    this.startingPromise = this.connection
      .start()
      .then(() => {
        this.zone.run(() => this._connected$.next(true));
      })
      .catch(err => {
        this.zone.run(() => this._connected$.next(false));
        throw err;
      })
      .finally(() => {
        this.startingPromise = null;
      });

    return this.startingPromise;
  }

  async disconnect(): Promise<void> {
    this.zone.run(() => this._connected$.next(false));

    if (!this.connection) return;

    try {
      await this.connection.stop();
    } catch {}
    finally {
      this.connection = null;
    }
  }

  // ==========================================================
  // Handlers
  // ==========================================================
  private registerHandlers(conn: HubConnection): void {

    conn.onreconnecting(() => {
      this.zone.run(() => this._connected$.next(false));
    });

    conn.onreconnected(() => {
      this.zone.run(() => this._connected$.next(true));
    });

    conn.onclose(() => {
      this.zone.run(() => this._connected$.next(false));
    });

    // EVENTO BACKEND
    conn.on('notify', (payload: SimpleNotification) => {
      this.zone.run(() => this.handleIncoming(payload));
    });
  }

  // ==========================================================
  // Procesar notificación (PERSISTENTE)
  // ==========================================================
  private handleIncoming(n: SimpleNotification): void {

    const stored: StoredNotification = {
      ...n,
      _id: this.createId(n),
      _ts: Date.now(),
      _read: false,
    };

    // ✅ evita duplicados (si backend reintenta)
    const current = this._notifications$.value;
    if (current.some(x => x._id === stored._id)) {
      return;
    }

    const next = [stored, ...current].slice(0, 100);

    this._notifications$.next(next);
    this.recalcUnread(next);
    this.saveToStorage(next);

    const paciente = (n?.nombrePaciente ?? '').toUpperCase();

    const text = paciente
      ? `TIENES UNA NUEVA NOTIFICACIÓN DE ${paciente}.`
      : 'TIENES UNA NUEVA NOTIFICACIÓN.';

    const snackRef = this.snack.open(text, 'VER', {
      duration: 6000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['rmd-snack-info'],
    });

// 👉 Cuando presionan VER
    snackRef.onAction().subscribe(() => {
      // Abrimos el modal usando el mismo componente
      this.zone.run(() => {
        this.dialog.open(NotificationDetailDialogComponent, {
          data: { notification: n },
          width: '92vw',
          maxWidth: '560px',
          panelClass: ['rmd-dialog-premium'],
          autoFocus: false
        });

        if ((n as any)?._id) {
          this.markAsReadAndRemove((n as any)._id);
        }
      });
    });

  }

  // ==========================================================
  // Public API para UI
  // ==========================================================

  /**
   * ✅ Cuando abres el modal (leer una)
   * La eliminamos (porque ya fue leída).
   */
  markAsReadAndRemove(id: string): void {
    const current = this._notifications$.value;
    const next = current.filter(x => x._id !== id);

    this._notifications$.next(next);
    this.recalcUnread(next);
    this.saveToStorage(next);
  }

  /**
   * ✅ Marcar todas como leídas = eliminar todas
   */
  markAllAsRead(): void {
    this._notifications$.next([]);
    this._unreadCount$.next(0);
    this.saveToStorage([]);
  }

  clear(): void {
    this.markAllAsRead();
  }

  // ==========================================================
  // Storage
  // ==========================================================
  private restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw) as StoredNotification[];
      if (!Array.isArray(parsed)) return;

      // sanity: solo las no leídas (por tu regla)
      const onlyUnread = parsed.filter(x => x && x._id && x._read === false);

      this._notifications$.next(onlyUnread);
      this.recalcUnread(onlyUnread);
    } catch {
      // si hay algo corrupto, limpiamos
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private saveToStorage(list: StoredNotification[]): void {
    try {
      // guardamos solo no leídas (lo que quieres)
      const onlyUnread = list.filter(x => x._read === false);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(onlyUnread));
    } catch {}
  }

  private recalcUnread(list: StoredNotification[]): void {
    const count = list.filter(x => x._read === false).length;
    this._unreadCount$.next(count);
  }

  // ==========================================================
  // Helpers
  // ==========================================================
  private buildHubUrl(): string {
    const base = (environment.backend_url ?? '').trim();
    const normalized = base.replace(/\/?api\/?$/i, '/');
    const hubPath = 'hubs/notificaciones';

    return normalized.endsWith('/')
      ? `${normalized}${hubPath}`
      : `${normalized}/${hubPath}`;
  }

  /**
   * Genera un id estable basado en contenido (sin librerías)
   * Si llega el mismo mensaje 2 veces, no lo duplica.
   */
  private createId(n: SimpleNotification): string {
    const raw = `${n?.titulo ?? ''}|${n?.mensaje ?? ''}|${n?.nombrePaciente ?? ''}`.trim().toLowerCase();
    return this.simpleHash(raw);
  }

  private simpleHash(input: string): string {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      hash = ((hash << 5) - hash) + input.charCodeAt(i);
      hash |= 0;
    }
    return `n_${Math.abs(hash)}`;
  }

  private getJwtToken(): string | null {
    return this.auth.getToken();
  }
}
